import * as Sentry from '@sentry/vue';
import '@stripe/stripe-js';
import { VueQueryPlugin } from '@tanstack/vue-query';
import { createPinia } from 'pinia';
import { createApp, type Component, type ComponentPublicInstance } from 'vue';
import 'vue-sonner/style.css';
import App from './app/App.vue';
import './app/assets/css/main.css';
import './app/assets/sass/app.scss';
import './app/plugins/momentEsLocale';
import { piniaResetPlugin } from './app/plugins/piniaReset.plugin';
import vuetify from './app/plugins/vuetify';
import router from './app/router';
import './app/scripts/loadScript';
import './app/validations/initValidations';
import { VERSION } from './version';
import { useGlobalPwaUpdate } from './app/pwa/composables/usePwaUpdate';

declare global {
  interface Window {
    process: {
      env: {
        NODE_ENV: string;
      };
    };
  }
}

const pinia = createPinia();
pinia.use(piniaResetPlugin);

const app = createApp(App);

app.use(pinia).use(router).use(vuetify).use(VueQueryPlugin);

//######## Sección referente a configuraciones de Sentry ########

//Configuraciones generales de Sentry
Sentry.init({
  app,
  dsn: 'https://59a842f1049349e387912f9c3923d7a5@o4504988110356480.ingest.sentry.io/4504988175892480',
  environment: import.meta.env.MODE,
  release: `munzen@${VERSION}`,

  // This sets the sample rate to be 30%. You may want this to be 100% while
  // in development and sample at a lower rate in production
  replaysSessionSampleRate: 0.2,

  // If the entire session is not sampled, use the below sample rate to sample
  // sessions when an error occurs.
  // It´s recommended to set this value to 1.0 in production
  replaysOnErrorSampleRate: 1.0,

  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for performance monitoring.
  // We recommend adjusting this value in production
  tracesSampleRate: 0.3,

  // Adds request headers and IP for users, for more info visit:
  // https://docs.sentry.io/platforms/javascript/guides/vue/configuration/options/#sendDefaultPii
  sendDefaultPii: true,

  // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: ['app.munzen.mx', 'api.munzen.mx'],

  integrations: [
    Sentry.browserTracingIntegration({ router }),
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: true,
    }),
  ],

  //Configuración para los errores de red: Para capturar los errores de red
  beforeSend(event: Sentry.ErrorEvent, hint: Sentry.EventHint) {
    if (import.meta.env.MODE !== 'production') {
      return null;
    }

    if (
      hint?.originalException &&
      (hint.originalException as { isAxiosError?: boolean }).isAxiosError
    ) {
      return null;
    }

    return event;
  },
});

// Error handler for Vue 3
app.config.errorHandler = (
  err: unknown,
  instance: ComponentPublicInstance | null,
  info: string,
) => {
  Sentry.withScope(scope => {
    if (instance) {
      const component = instance as Component & { type?: { name?: string } };
      scope.setExtra('component', component?.type?.name);
      scope.setExtra('props', (instance as ComponentPublicInstance & { $props?: unknown })?.$props);
    }
    scope.setExtra('info', info);
    Sentry.captureException(err);
  });
};

app.mount('#app');

/**
 * Register Service Worker for PWA functionality
 * This must be done after the app mounts to avoid blocking initial render
 * Implements callbacks for update detection and offline readiness
 * Integrates with PWA composables for state management
 */
if ('serviceWorker' in navigator) {
  // Get the global PWA update instance
  const pwaUpdate = useGlobalPwaUpdate();

  // Import registerSW from virtual:pwa-register (provided by vite-plugin-pwa)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (import('virtual:pwa-register') as Promise<any>).then((module: any) => {
    const { registerSW } = module;

    /**
     * Register the service worker with callbacks for:
     * - onNeedRefresh: Called when a new service worker is available
     * - onOfflineReady: Called when the app is ready to work offline
     * - onRegistered: Called when the service worker is successfully registered
     * - onRegisterError: Called if service worker registration fails
     */
    const updateSW = registerSW({
      immediate: true,
      onNeedRefresh() {
        // Set the needRefresh flag to trigger update prompt
        pwaUpdate.needRefresh.value = true;
        // Show the update prompt to the user
        pwaUpdate.showUpdatePrompt();
      },
      onOfflineReady() {
        // Set the offlineReady flag when app is ready to work offline
        pwaUpdate.offlineReady.value = true;
        console.log('PWA: App is ready to work offline');
      },
      onRegistered(registration: ServiceWorkerRegistration | undefined) {
        console.log('PWA: Service Worker registered successfully', registration);
      },
      onRegisterError(error: Error) {
        console.error('PWA: Service Worker registration failed:', error);
        // Report to Sentry in production
        if (import.meta.env.PROD) {
          Sentry.captureException(error, {
            tags: { component: 'service-worker-registration' },
          });
        }
      },
    });

    // Set the updateSW function in the composable so it can be called later
    if (pwaUpdate.setUpdateSW) {
      pwaUpdate.setUpdateSW(updateSW);
    }
  });
}
